/**
 * Self-hosted Google Fonts URL builders.
 *
 * These replace Google Fonts CSS URLs with Joy-hosted equivalents.
 * Each font family has one index.css containing all @font-face rules
 * for all weights, styles, and subsets. The browser selects which
 * .woff2 binaries to download via font-weight/font-style matching
 * and unicode-range.
 *
 * Assets are published by the mirror script: scripts/font-mirror/
 * CDN path: https://withjoy.com/assets/public/fonts/google/{slug}/
 */
import { SELF_HOSTED_FONTS_BASE } from '@shared/utils/urls';

/**
 * Known font family name inconsistencies between Joy's database and Google Fonts.
 * Must stay in sync with scripts/font-mirror/normalize.ts.
 */
const KNOWN_ALIASES: Readonly<Record<string, string>> = {
  UnifrakturCook: 'UnifrakturMaguntia',
  SirinStencil: 'Sirin Stencil',
  'Playwrite US Traditional': 'Playwrite USA Traditional',
  Thasadtih: 'Thasadith',
};

/**
 * Normalize a font family name by applying known aliases.
 * The mirrored catalog uses normalized names, but event data may carry legacy aliases.
 */
export const normalizeFontFamily = (family: string): string => {
  return KNOWN_ALIASES[family] ?? family;
};

const slugify = (family: string): string => {
  return normalizeFontFamily(family).toLowerCase().replace(/\s+/g, '-');
};

/**
 * Build the Joy CDN URL for a font family's index.css.
 * Normalizes legacy aliases automatically (e.g., "SirinStencil" → sirin-stencil/index.css).
 */
export const buildSelfHostedFontCssUrl = (family: string): string => {
  const slug = slugify(family);
  return `${SELF_HOSTED_FONTS_BASE}/${slug}/index.css`;
};

/**
 * Build direct .woff2 preload URLs for a font family's critical initial render.
 * Returns latin normal 400 and 700 only — the minimal subset for above-the-fold text.
 * All other weights/styles/subsets load on demand via the CSS unicode-range mechanism.
 *
 * Some single-weight fonts (e.g., Pacifico) don't have a 700 variant — the preload
 * for the missing weight returns a fast 404 from CloudFront. This is intentional:
 * the cost of a ~100-byte 404 is negligible, and the font still renders correctly
 * because index.css only declares @font-face for weights that actually exist (the
 * browser synthesizes bold from 400). The alternative — encoding per-family weight
 * availability into the runtime — would add complexity without meaningful benefit.
 */
export const buildCriticalPreloadUrls = (family: string): ReadonlyArray<string> => {
  const slug = slugify(family);
  return [`${SELF_HOSTED_FONTS_BASE}/${slug}/${slug}-400-normal-latin.woff2`, `${SELF_HOSTED_FONTS_BASE}/${slug}/${slug}-700-normal-latin.woff2`];
};
